import { stripTrailingSlashes } from '@shared/utils/strings'

const REALM_NAME = 'unzer_merchants'
const REALM = `/realms/${REALM_NAME}`
const AUTH_PATH = '/protocol/openid-connect/auth'
const LOGOUT_PATH = '/protocol/openid-connect/logout'
const CLIENT_ID = 'unzerone-dashboard'
const RESPONSE_TYPE = 'code'
const SCOPE = 'openid'

interface MerchantConfig {
  domain: string
  authBaseUrl: string
  dashboardUrl: string
  idpHint: string
}

export interface SSOConfig {
  redirectUrl: string
  dashboardUrl: string
  authBaseUrl: string
  idpHint: string
}

const MERCHANT_CONFIGS: readonly MerchantConfig[] = [
  {
    domain: 'redbullmuenchen.unzer.com',
    authBaseUrl: 'https://auth.unzer.com',
    dashboardUrl: 'https://redbullmuenchen.unzer.com',
    idpHint: 'UZ2563823-idp'
  },
  {
    domain: 'redbullmuenchen.test.unzer.com',
    authBaseUrl: 'https://auth.test.unzer.com',
    dashboardUrl: 'https://redbullmuenchen.test.unzer.com',
    idpHint: 'UZ9977766-idp'
  },
  {
    domain: 'redbullmuenchen.int.unzer.io',
    authBaseUrl: 'https://auth.int.unzer.io',
    dashboardUrl: 'https://redbullmuenchen.int.unzer.io',
    idpHint: 'UZ9713245-idp'
  },
  {
    domain: 'rbleipzig.unzer.com',
    authBaseUrl: 'https://auth.unzer.com',
    dashboardUrl: 'https://rbleipzig.unzer.com',
    idpHint: 'UZ3426381-idp'
  },
  {
    domain: 'rbleipzig.test.unzer.com',
    authBaseUrl: 'https://auth.test.unzer.com',
    dashboardUrl: 'https://rbleipzig.test.unzer.com',
    idpHint: 'UZ8242825-idp'
  }
] as const

const buildAuthUrl = (
  authBaseUrl: string,
  dashboardUrl: string,
  idpHint: string
): string => {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: RESPONSE_TYPE,
    scope: SCOPE,
    kc_idp_hint: idpHint,
    // Keycloak matches this character by character, and the adapter sends the
    // origin with a trailing slash. Spell it the same way here.
    redirect_uri: `${stripTrailingSlashes(dashboardUrl)}/`
  })
  return `${stripTrailingSlashes(authBaseUrl)}${REALM}${AUTH_PATH}?${params}`
}

export const buildSSOLogoutUrl = (
  authBaseUrl: string,
  redirectUri: string,
  idToken: string
): string => {
  const encodedRedirectUri = encodeURIComponent(redirectUri)
  return (
    `${stripTrailingSlashes(authBaseUrl)}${REALM}${LOGOUT_PATH}` +
    `?client_id=${encodeURIComponent(CLIENT_ID)}` +
    `&post_logout_redirect_uri=${encodedRedirectUri}` +
    `&id_token_hint=${idToken}`
  )
}

const getMerchantConfig = (host?: string): MerchantConfig | null => {
  const currentHost = host || window.location.host
  return (
    MERCHANT_CONFIGS.find((config) => config.domain === currentHost) || null
  )
}

export const getSSOConfig = (host?: string): SSOConfig | null => {
  const config = getMerchantConfig(host)

  if (!config) {
    return null
  }

  return {
    redirectUrl: buildAuthUrl(
      config.authBaseUrl,
      config.dashboardUrl,
      config.idpHint
    ),
    dashboardUrl: config.dashboardUrl,
    authBaseUrl: config.authBaseUrl,
    idpHint: config.idpHint
  }
}

export const isMerchantSSODomain = (host?: string): boolean => {
  return getMerchantConfig(host) !== null
}
